/* eslint-disable no-underscore-dangle */
import './coac.d';
import {
  get,
  post,
} from '../http';
import {
  clearCoacJourneyActive,
  markCoacJourneyActive,
} from '../journeyState';

/**
 * An API layer for COAC API requests
 */
export default class COAC {
  /**
   * @param {String} api - the fully qualified domain to issue requests against
   * @param {String} postbackURL - success redirect from payment
   */
  constructor({
    api = null,
    postbackURL = '',
  }) {
    /**
     * The fully qualified base url for the MGO API
     * @type {String}
     */
    this.api = api;

    /**
     * Used during the Global Payment handoff to redirect on success
     * @type {String}
     */
    this.postbackURL = postbackURL;

    /**
     * COA order confirmation code
     * @type {String}
     */
    this.cfpsId = sessionStorage.getItem('coaccfpsid') || '';

    /**
     * The new zip code used to login
     * @type {String}
     */
    this.loginZip = sessionStorage.getItem('coaccode') || '';

    /**
     * The Visit Id of the current session
     * @type {Number}
     */
    this.visitId = null;

    /**
     * Number of login attempts made
     * @type {Number}
     */
    this.numLoginAttempts = 0;
  }

  reset() {
    this.numLoginAttempts = 0;
    this.cfpsId = '';
    this.loginZip = '';
    this.visitId = null;
    clearCoacJourneyActive();
    sessionStorage.removeItem('coacv');
    sessionStorage.removeItem('coaccfpsid');
    sessionStorage.removeItem('coaccode');
    sessionStorage.removeItem('coacUuid');
    sessionStorage.removeItem('coacext');
  }

  /**
   * Logout users
   */
  logout() {
    this.reset();
  }

  /**
   * Retrieve Application Configuration Details
   * These config items relate to Command Center
   * @method configGet
   * @return {Promise<Config>}
   */
  async configGet() {
    return get(`${this.api}/mgo-api/api/v1/app/config`, {}, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  /**
   * COAC Actions
   * ----------------------------------------------------------------
   */

  /**
   * Login to existing COA order
   * @param {String} cfpsId - 16 digit COA order confirmation code
   * @param {String} zip5 - COA order new address zip5
   * @param {String} deviceType - DESKTOP | MOBILE
   * @return {Promise<COA>}
   */
  async login(cfpsId, zip5, deviceType) {
    clearCoacJourneyActive();
    const raw = await post(`${this.api}/mgo-api/api/v1/coac/login?deviceType=${deviceType}`, {
      cfpsId,
      numAttempts: this.numLoginAttempts,
      zip5,
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });

    if (raw?.data?.status === 500) {
      return Promise.resolve('There was an issue processing your request. Please try again.');
    }

    if (raw?.error || raw?.xhr?.error) {
      this.numLoginAttempts += 1;
      return Promise.resolve(raw?.xhr?.message);
    }

    this.numLoginAttempts = 0;
    this.cfpsId = cfpsId;
    this.loginZip = zip5;

    sessionStorage.setItem('coaccfpsid', cfpsId);
    sessionStorage.setItem('coaccode', zip5);
    sessionStorage.setItem('visitId', raw.visitId);
    if (raw.coaOrderUuid) {
      sessionStorage.setItem('coacUuid', raw.coaOrderUuid);
    } else {
      sessionStorage.removeItem('coacUuid');
    }
    this.visitId = raw.visitId;
    markCoacJourneyActive();

    return Promise.resolve('');
  }

  /**
   * Search for order COA data
   * @return {Promise<COA>}
   */
  async search() {
    let response;
    try {
      response = await post(`${this.api}/mgo-api/api/v1/coac/search`, {
        cfpsId: this.cfpsId,
        visitId: this.visitId,
        zip5: this.loginZip,
      }, {
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      });
    } catch (error) {
      return { error: true, xhr: error?.xhr, message: error?.message };
    }
    if (response?.error || response?.xhr?.error || !response?.cfpsId) {
      return response;
    }

    this.modCoacCfpsId = response.modCoacCfpsId;
    if (response.coaOrderUuid) {
      this.cfpsId = response.cfpsId;
      sessionStorage.setItem('coaccfpsid', response.cfpsId);
      sessionStorage.setItem('coacUuid', response.coaOrderUuid);
    }
    return response;
  }

  async verifyActivationCode({ activationPasscode }) {
    try {
      const response = await post(`${this.api}/mgo-api/api/v1/coac/activate`, {
        cfpsId: this.cfpsId,
        mgoActivationCode: activationPasscode,
        visitId: this.visitId,
      });
      if (!response?.error && !response?.xhr?.error) {
        return response;
      }
      const passcodeException = response?.xhr?.exception || '';
      return { error: true, message: response?.xhr?.message, passcodeException };
    } catch (error) {
      return Promise.resolve({ error: true, message: error?.message });
    }
  }

  /**
    * Save a COA correction
    * @param {String} firstName
    * @param {String} lastName
    * @param {String} email
    * @param {String} homePhone
    * @param {String} mobilePhone
    * @param {String} moverType - INDIVIDUAL | FAMILY | BUSINESS
    * @param {String} forwardType - PERMANENT | TEMPORARY
    * @param {String} startDate
    * @param {String} endDate - TEMPORARY filers
    * @param {Address} oldAddress
    * @param {Address} newAddress
    * @param {Boolean} coacInformedDeliveryOptIn - opted into informed delivery in COAC flow
    * @return {Promise<COA>}
    */
  async save({
    firstName,
    middleName,
    lastName,
    suffix,
    email,
    homePhone = null,
    mobilePhone = null,
    moverType,
    forwardType,
    startDate,
    endDate = null,
    oldAddress,
    newAddress,
    coacInformedDeliveryOptIn = false,
  }) {
    const response = await post(`${this.api}/mgo-api/api/v1/coac/save`, {
      cfpsId: this.cfpsId,
      visitId: this.visitId,
      zip5: this.loginZip,
      firstName,
      middleName,
      lastName,
      suffix,
      email,
      homePhone,
      mobilePhone,
      moverType,
      forwardType,
      startDate,
      stopDate: endDate, // TODO coordinate with BE to make this consistent with MGO
      oldAddress,
      newAddress,
      coacInformedDeliveryOptIn,
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });

    if (!response?.error && !response?.xhr?.error) {
      if (response.cfpsId) {
        this.cfpsId = response.cfpsId;
        sessionStorage.setItem('coaccfpsid', response.cfpsId);
      }
      if (response.coaOrderUuid) sessionStorage.setItem('coacUuid', response.coaOrderUuid);
    }
    return response;
  }

  /**
   * Cancel a COA order
   * @property {String} reasonCode - CAN | FRD | NAME | ADDR
   * @return {Promise<Boolean>}
   */
  async cancel(reasonCode) {
    const { error } = await post(`${this.api}/mgo-api/api/v1/coac/cancel`, {
      cancelReason: reasonCode,
      cfpsId: this.cfpsId,
      visitId: this.visitId,
      zip5: this.loginZip,
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
    return Promise.resolve(!error);
  }

  /**
   * EMF Actions
   * ----------------------------------------------------------------
   */

  /**
   * Get Global Payment forwarding Information
   * @param {String} returnUrl - The url to return a user to if they use GP Back
   * @param {Number} optionId - selected extension option
   * @return {Promise<GlobalPaymentResponse>}
   */
  async globalPaymentURLGet({ returnUrl, optionId }) {
    return post(`${this.api}/mgo-api/api/v1/coac/payment`, {
      cfpsId: this.cfpsId,
      newZip5: this.loginZip,
      optionId,
      postbackUrl: this.postbackURL,
      returnUrl,
      visitId: this.visitId,
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  /**
   * Verify EMF order after payment
   * @param {String} extensionId - EMF UUID
   * @return {Promise}
   */
  async orderCommit(extensionId) {
    const url = `${this.api}/mgo-api/api/v1/coac/payment/commit`;
    const item = url.split('?');
    const outUrl = url?.replace(`?${item[1]}`, `/commit?${item[1]?.replace('/commit', '')}`);

    const resp = post(outUrl, {
      extensionId,
      cfpsId: this.cfpsId,
      newZip5: this.loginZip,
      visitId: this.visitId,
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
    sessionStorage.removeItem('visitId');
    return resp;
  }

  /**
   * MYMOVE Actions
   * ----------------------------------------------------------------
   */

  /**
   * Get Access code to build handoff url
   * @return {Promise<HandoffCodeResponse>}
   */
  async handoffCodeGet() {
    return get(
      `${this.api}/mgo-api/api/v1/handoffs?cfpsId=${this.cfpsId}&newZip=${this.loginZip}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      },
    );
  }

  /**
   * Analytics Events
   * ----------------------------------------------------------------
   */

  /**
   * Get the visitId
   * @param {String} deviceType - DESKTOP | MOBILE
   * @param {String} queryString - url query string
   * @return {Promise<Number>}
   */
  async visit(deviceType, queryString) {
    const visitId = JSON.parse(sessionStorage.getItem('visitId'));
    if (!visitId) {
      this.visitId = await post(
        `${this.api}/mgo-api/api/v1/e/v`,
        {
          params: {
            qs: queryString,
            deviceType,
          },
        },
        {
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        },
      );
      sessionStorage.setItem('visitId', this.visitId);
    }
    this.visitId = visitId;
    return Promise.resolve(this.visitId);
  }

  async logExceptions({
    exception,
    oldAddress,
    newAddress,
    zip5,
    startDate,
    stopDate,
  }) {
    const resp = post(`${this.api}/mgo-api/api/v1/exception/coac`, {
      exception,
      oldAddress,
      newAddress,
      cfpsId: this.cfpsId,
      zip5,
      startDate,
      stopDate,
      visitId: this.visitId,
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
    return resp;
  }
}
